// eslint-disable-next-line no-restricted-imports -- @klear/time uses dayjs only for UTC format/parse helpers inside this module
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { KlearDate } from '@klear/date'
import isFinite from 'lodash/isFinite'
import isNumber from 'lodash/isNumber'
import toNumber from 'lodash/toNumber'

dayjs.extend(utc)

function assertFiniteMs(ms: number): number {
  if (!isNumber(ms) || !isFinite(ms)) {
    throw new Error(`Timestamp: expected finite epoch milliseconds, got ${ms}`)
  }
  return Math.trunc(toNumber(ms))
}

export type TimestampUnit =
  | 'millisecond'
  | 'milliseconds'
  | 'second'
  | 'seconds'
  | 'minute'
  | 'minutes'
  | 'hour'
  | 'hours'
  | 'day'
  | 'days'

const MS_PER_UNIT: Record<string, number> = {
  millisecond: 1,
  second: 1_000,
  minute: 60_000,
  hour: 3_600_000,
  day: 86_400_000
}

function unitMs(unit: TimestampUnit): number {
  const ms = MS_PER_UNIT[unit.replace(/s$/, '')]
  if (ms === undefined) throw new Error(`Unsupported Timestamp unit: ${unit}`)
  return ms
}

export function timestampToJson(timestamp: Timestamp): number {
  return timestamp.epochMs
}
export type TimestampJson = ReturnType<typeof timestampToJson>

export class Timestamp {
  static readonly __klearClass = 'Timestamp'
  static readonly DEFAULT_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ'

  readonly epochMs: number

  static is(value: unknown): value is Timestamp {
    return value instanceof Timestamp
  }

  static now(): Timestamp {
    // eslint-disable-next-line no-restricted-globals -- wall clock for Timestamp.now()
    return new Timestamp(Date.now())
  }

  static unix(seconds?: number | null): Timestamp {
    return Timestamp.fromEpochSeconds(seconds ?? 0)
  }

  static fromEpochSeconds(seconds: number): Timestamp {
    if (!isNumber(seconds) || !isFinite(seconds)) {
      throw new Error(`Timestamp.fromEpochSeconds: expected finite number, got ${seconds}`)
    }
    return new Timestamp(Math.trunc(toNumber(seconds) * 1000))
  }

  static fromEpochMs(ms: number): Timestamp {
    return new Timestamp(ms)
  }

  static timestamp(): number {
    return Timestamp.now().unix()
  }

  static timestampMs(): number {
    return Timestamp.now().epochMs
  }

  static min(a: Timestamp, b: Timestamp): Timestamp {
    return a.epochMs <= b.epochMs ? a : b
  }

  static max(a: Timestamp, b: Timestamp): Timestamp {
    return a.epochMs >= b.epochMs ? a : b
  }

  static startOfUtcDay(date: KlearDate): Timestamp {
    return new Timestamp(dayjs.utc(`${date.toJSON()}T00:00:00.000Z`).valueOf())
  }

  static endOfUtcDay(date: KlearDate): Timestamp {
    return new Timestamp(dayjs.utc(`${date.toJSON()}T23:59:59.999Z`).valueOf())
  }

  constructor(epochMs: TimestampJson | Timestamp) {
    this.epochMs = epochMs instanceof Timestamp ? epochMs.epochMs : assertFiniteMs(epochMs)
  }

  unix(): number {
    return Math.trunc(this.epochMs / 1000)
  }

  add(amount: number, unit: TimestampUnit): Timestamp {
    return new Timestamp(assertFiniteMs(this.epochMs + amount * unitMs(unit)))
  }

  subtract(amount: number, unit: TimestampUnit): Timestamp {
    return new Timestamp(assertFiniteMs(this.epochMs - amount * unitMs(unit)))
  }

  diff(other: Timestamp, unit: TimestampUnit = 'millisecond'): number {
    const ms = this.epochMs - other.epochMs
    return unit === 'millisecond' || unit === 'milliseconds' ? ms : ms / unitMs(unit)
  }

  equals(other: Timestamp): boolean {
    return this.epochMs === other.epochMs
  }

  isBefore(other: Timestamp): boolean {
    return this.epochMs < other.epochMs
  }

  isAfter(other: Timestamp): boolean {
    return this.epochMs > other.epochMs
  }

  isSameOrBefore(other: Timestamp): boolean {
    return this.epochMs <= other.epochMs
  }

  isSameOrAfter(other: Timestamp): boolean {
    return this.epochMs >= other.epochMs
  }

  toString(format: string = Timestamp.DEFAULT_FORMAT): string {
    return dayjs.utc(this.epochMs).format(format)
  }

  toKlearDate(): KlearDate {
    return KlearDate.of(this.toString(KlearDate.YMD_FORMAT))
  }

  toLocalString(format: string = Timestamp.DEFAULT_FORMAT): string {
    return dayjs(this.epochMs).format(format)
  }

  toLocalKlearDate(): KlearDate {
    return KlearDate.of(this.toLocalString(KlearDate.YMD_FORMAT))
  }

  toIso8601(): string {
    return dayjs.utc(this.epochMs).toISOString()
  }

  toJsDate(): Date {
    // eslint-disable-next-line no-restricted-globals -- pg driver interop
    return new Date(this.epochMs)
  }

  toJSON(): TimestampJson {
    return timestampToJson(this)
  }

  valueOf(): number {
    return this.epochMs
  }
}
